import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { usePlaidLink } from "react-plaid-link";
import { api } from "@/lib/api";
import { BkPage, Card, SmallBtn, fmtMoney, fmtTime } from "@/components/bookkeeping/BkPage";

const Pill = ({ ok, children, testId }) => (
  <span data-testid={testId} className="rounded-full border px-2.5 py-0.5 text-[11px] font-extrabold" style={{ color: ok ? "#4CCC06" : "#F5B700", borderColor: ok ? "#4CCC06" : "#F5B700" }}>{children}</span>
);

const QboCard = ({ entity, q, reload }) => {
  const c = q.connection;
  const connected = c?.status === "connected";
  const go = async () => { try { window.location.assign((await api(`/bookkeeping/qbo/connect?entity_id=${entity.id}`)).url); } catch (e) { toast.error(e.message); } };
  const sync = async () => { await api(`/bookkeeping/qbo/${entity.id}/sync-reference`, { method: "POST" }); toast.success("Refreshing chart of accounts"); setTimeout(reload, 4000); };
  const off = async () => { if (!window.confirm("Disconnect QuickBooks for this entity?")) return; await api(`/bookkeeping/qbo/${entity.id}/disconnect`, { method: "POST" }); reload(); };
  if (!entity.has_qbo_file) return <Card title="QUICKBOOKS" testId="bk-qbo-card"><p className="text-sm text-neutral-400">No QuickBooks file for this entity.</p></Card>;
  return (
    <Card title={`QUICKBOOKS · ${q.environment.toUpperCase()}`} testId="bk-qbo-card"
      right={<Pill ok={connected} testId="bk-qbo-status">{!q.configured ? "Keys needed" : connected ? "Connected" : c?.status === "reconnect_required" ? "Reconnect needed" : "Not connected"}</Pill>}>
      {!q.configured && <p className="text-sm text-neutral-400" data-testid="bk-qbo-keys-needed">QuickBooks development keys haven't been added yet. Connecting unlocks once they are.</p>}
      {c?.status === "reconnect_required" && <p className="mb-2 text-sm text-[#F5B700]" data-testid="bk-qbo-reconnect-banner">QuickBooks stopped accepting the saved sign-in. Reconnect to resume syncing.</p>}
      {connected && <p className="text-sm text-neutral-300">Company ID {c.realm_id} · chart of accounts synced {fmtTime(c.chart_of_accounts_synced_at)}{c.reference_counts ? ` · ${c.reference_counts.Account} accounts, ${c.reference_counts.Vendor} vendors, ${c.reference_counts.Customer} customers` : ""}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <SmallBtn primary disabled={!q.configured} onClick={go} data-testid="bk-qbo-connect">{connected ? "Reconnect" : "Connect QuickBooks"}</SmallBtn>
        {connected && <SmallBtn onClick={sync} data-testid="bk-qbo-sync">Refresh chart of accounts</SmallBtn>}
        {connected && <SmallBtn onClick={off} data-testid="bk-qbo-disconnect">Disconnect</SmallBtn>}
      </div>
    </Card>
  );
};

const PlaidButton = ({ entityId, itemId, label, onDone, disabled, testId }) => {
  const [token, setToken] = useState(null);
  const { open, ready } = usePlaidLink({
    token,
    onSuccess: async (public_token, meta) => {
      if (!itemId) await api("/bookkeeping/plaid/exchange", { method: "POST", body: { entity_id: entityId, public_token, institution: meta.institution?.name } });
      toast.success(itemId ? "Bank reconnected" : "Bank connected, first sync running"); setToken(null); onDone();
    },
    onExit: () => setToken(null),
  });
  useEffect(() => { if (token && ready) open(); }, [token, ready, open]);
  const start = async () => {
    try { setToken((await api(`/bookkeeping/plaid/link-token?entity_id=${entityId}${itemId ? `&item_id=${itemId}` : ""}`, { method: "POST" })).link_token); }
    catch (e) { toast.error(e.message); }
  };
  return <SmallBtn primary={!itemId} disabled={disabled} onClick={start} data-testid={testId}>{label}</SmallBtn>;
};

const BankCard = ({ entity, data, reload }) => {
  const p = data.plaid;
  const act = async (path, msg) => { try { await api(path, { method: "POST" }); toast.success(msg); setTimeout(reload, 5000); } catch (e) { toast.error(e.message); } };
  return (
    <Card title={`BANK FEEDS · PLAID ${p.environment.toUpperCase()}`} testId="bk-bank-card" right={<Pill ok={p.configured} testId="bk-plaid-status">{p.configured ? `${p.items.length} bank login${p.items.length === 1 ? "" : "s"}` : "Keys needed"}</Pill>}>
      {!p.configured && <p className="mb-2 text-sm text-neutral-400" data-testid="bk-plaid-keys-needed">Plaid keys haven't been added yet. Bank connections unlock once they are.</p>}
      {p.items.filter((i) => i.status === "login_required").map((i) => (
        <div key={i.item_id} className="mb-2 flex items-center justify-between rounded-lg border border-[#F5B700] p-3 text-sm text-[#F5B700]" data-testid={`bk-plaid-reconnect-${i.item_id}`}>
          {i.institution} needs you to sign in again.
          <PlaidButton entityId={entity.id} itemId={i.item_id} label="Reconnect" onDone={reload} testId={`bk-plaid-reconnect-btn-${i.item_id}`} />
        </div>
      ))}
      <div className="divide-y divide-[#262626]" data-testid="bk-bank-accounts">
        {data.bank_accounts.map((a) => (
          <div key={a.id} className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid={`bk-bank-account-${a.id}`}>
            <span className="min-w-0 flex-1 font-bold text-white">{a.institution} · {a.name} ••{a.mask} <span className="font-normal text-neutral-500">({a.type})</span></span>
            <span className="text-neutral-300">{fmtMoney(a.current_balance)}</span>
            <span className="text-xs text-neutral-500">synced {fmtTime(a.last_synced_at)}</span>
          </div>
        ))}
        {data.bank_accounts.length === 0 && <p className="py-2 text-sm text-neutral-500">No bank accounts linked yet.</p>}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <PlaidButton entityId={entity.id} label="Connect a bank" disabled={!p.configured} onDone={reload} testId="bk-plaid-connect" />
        <SmallBtn disabled={!p.items.length} onClick={() => act(`/bookkeeping/bank/sync?entity_id=${entity.id}`, "Sync started")} data-testid="bk-bank-sync">Sync now</SmallBtn>
        {p.environment === "sandbox" && <SmallBtn disabled={!p.configured} onClick={() => act(`/bookkeeping/plaid/sandbox-seed?entity_id=${entity.id}`, "Test transactions are being added")} data-testid="bk-plaid-seed">Add sandbox test transactions</SmallBtn>}
      </div>
    </Card>
  );
};

const Body = ({ entity }) => {
  const [data, setData] = useState(null);
  const [params] = useSearchParams();
  const load = useCallback(() => api(`/bookkeeping/connections?entity_id=${entity.id}`).then(setData).catch((e) => toast.error(e.message)), [entity.id]);
  useEffect(() => { setData(null); load(); }, [load]);
  useEffect(() => {
    if (params.get("qbo_connected")) toast.success("QuickBooks connected. Pulling the chart of accounts…");
    if (params.get("qbo_error")) toast.error(`QuickBooks: ${params.get("qbo_error")}`);
  }, [params]);
  if (!data) return null;
  return <div className="grid gap-4 lg:grid-cols-2"><QboCard entity={entity} q={data.qbo} reload={load} /><BankCard entity={entity} data={data} reload={load} /></div>;
};

export default function Connections() {
  return <BkPage title="Connections" testId="bookkeeping-connections">{({ entity }) => <Body entity={entity} />}</BkPage>;
}
